import { logger } from 'utils/logging'

/**
 * The conversation the user has open in the chat widget, shared by every tab of the same browser.
 *
 * localStorage rather than sessionStorage because the latter is per tab, and a support chat is one ongoing
 * conversation, not per-tab state (QA-377). Only the conversation id is stored, keyed by the platform user id
 * so a different user signing in on the same browser never inherits it.
 */
const storageKey = (userId: string) => `bkbn.chatWidget.openConversationId.${userId}`

/** Returns the stored open conversation id, or null when there is none or storage is unavailable. */
export const readOpenConversationId = (userId: string | undefined): string | null => {
  if (!userId) return null
  try {
    return window.localStorage.getItem(storageKey(userId))
  } catch (error) {
    logStorageUnavailable('read', error)
    return null
  }
}

/** Stores the open conversation id, or forgets it when `conversationId` is null. */
export const writeOpenConversationId = (userId: string | undefined, conversationId: string | null): void => {
  if (!userId) return
  try {
    if (conversationId) window.localStorage.setItem(storageKey(userId), conversationId)
    else window.localStorage.removeItem(storageKey(userId))
  } catch (error) {
    logStorageUnavailable('write', error)
  }
}

// Safari private mode, blocked site data or a partner iframe denied storage: the widget still works, it
// just cannot restore the open conversation in another tab.
const logStorageUnavailable = (action: 'read' | 'write', error: unknown) => {
  logger.info('ChatWidget: open conversation storage unavailable', error, {
    context: { component: 'ChatWidget', action: `openConversationStorage.${action}` },
  })
}
